import {
  type PanelLayoutTransition,
  type PanelSlide,
  RESIZE_PANEL_SLIDE_TIMING,
  ResizePanel,
} from '@affine/component/resize-panel';
import { AffineErrorComponent } from '@affine/core/components/affine/affine-error-boundary/affine-error-fallback';
import { useSidebarFloats } from '@affine/core/components/hooks/use-sidebar-floats';
import { SidebarFloatingMask } from '@affine/core/components/sidebar-floating-mask';
import { ChatPanelActiveViewOwner } from '@affine/core/desktop/pages/workspace/layouts/chat-panel-active-view-owner';
import { WorkspaceChatOverlay } from '@affine/core/desktop/pages/workspace/layouts/workspace-chat-sidebar-tab';
import {
  loadLocalFirstRouteModules,
  workbenchRoutes,
} from '@affine/core/desktop/workbench-router';
import { useI18n } from '@affine/i18n';
import {
  appSettingAtom,
  FrameworkScope,
  useLiveData,
  useService,
} from '@toeverything/infra';
import { assignInlineVars } from '@vanilla-extract/dynamic';
import { useAtomValue } from 'jotai';
import {
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { type RouteObject, useLocation } from 'react-router-dom';

import type { View } from '../entities/view';
import { WorkbenchService } from '../services/workbench';
import { useBindWorkbenchToBrowserRouter } from './browser-adapter';
import { useBindWorkbenchToDesktopRouter } from './desktop-adapter';
import { RouteContainer } from './route-container';
import { SidebarContainer } from './sidebar/sidebar-container';
import { useSidebarCover } from './sidebar-cover';
import {
  sidebarSlideTime,
  sidebarSlideViews,
  sidebarSlideWidth,
} from './sidebar-slide.css';
import { SplitView } from './split-view/split-view';
import {
  ViewCopilotOverlayTarget,
  ViewIslandRegistryProvider,
} from './view-islands';
import { ViewRoot } from './view-root';
import * as styles from './workbench-root.css';

const useAdapter = BUILD_CONFIG.isElectron
  ? useBindWorkbenchToDesktopRouter
  : useBindWorkbenchToBrowserRouter;

const defaultRoutes: RouteObject[] = [
  {
    element: <RouteContainer />,
    errorElement: <AffineErrorComponent />,
    children: workbenchRoutes,
  },
];

const MIN_SIDEBAR_WIDTH = 312;
const MAX_SIDEBAR_WIDTH = 1400;
const dockedSidebarWidth = (width: number) =>
  Math.min(MAX_SIDEBAR_WIDTH, Math.max(MIN_SIDEBAR_WIDTH, width));

export const WorkbenchRoot = memo(
  ({
    basename: basenameOverride,
    sidebar = true,
    workspaceChatOverlay = true,
  }: {
    basename?: string;
    sidebar?: boolean;
    workspaceChatOverlay?: boolean;
  }) => {
    const workbench = useService(WorkbenchService).workbench;

    // for debugging
    (window as any).workbench = workbench;

    const views = useLiveData(workbench.views$);
    const sidebarWidth = useLiveData(workbench.sidebarWidth$);
    const sidebarSlideVars = useMemo(
      () =>
        assignInlineVars({
          [sidebarSlideWidth]: `${dockedSidebarWidth(sidebarWidth ?? 0)}px`,
          [sidebarSlideTime]: RESIZE_PANEL_SLIDE_TIMING,
        }),
      [sidebarWidth]
    );
    const [sidebarSlide, setSidebarSlide] = useState<SidebarSlide>({
      layout: 'continuous',
      covering: false,
      sliding: false,
    });
    const sidebarCovers = (layout: PanelLayoutTransition) =>
      (sidebar && sidebarSlide.covering && sidebarSlide.layout === layout) ||
      undefined;
    const location = useLocation();
    const basename =
      basenameOverride ??
      location.pathname.match(/\/workspace\/[^/]+/g)?.[0] ??
      '/';

    useAdapter(workbench, basename);

    const panelRenderer = useCallback((view: View) => {
      return <WorkbenchView view={view} />;
    }, []);

    const onMove = useCallback(
      (from: number, to: number) => {
        workbench.moveView(from, to);
      },
      [workbench]
    );

    useEffect(() => {
      workbench.updateBasename(basename);
    }, [basename, workbench]);

    useEffect(() => {
      loadLocalFirstRouteModules();
    }, []);

    return (
      <ViewIslandRegistryProvider>
        {workspaceChatOverlay ? (
          <>
            <ChatPanelActiveViewOwner />
            <WorkspaceChatOverlay />
          </>
        ) : null}
        <SplitView
          className={`${styles.workbenchRootContainer} ${sidebarSlideViews}`}
          style={sidebarSlideVars}
          data-sidebar-covering={sidebarCovers('settled')}
          data-sidebar-overlaying={sidebarCovers('overlay')}
          data-sidebar-sliding={(sidebar && sidebarSlide.sliding) || undefined}
          views={views}
          renderer={panelRenderer}
          onMove={onMove}
        />
        {sidebar ? (
          <WorkbenchSidebar
            onSlide={setSidebarSlide}
            overlaying={Boolean(sidebarCovers('overlay'))}
          />
        ) : null}
      </ViewIslandRegistryProvider>
    );
  }
);

WorkbenchRoot.displayName = 'memo(WorkbenchRoot)';

export const WorkbenchView = ({
  view,
  routes = defaultRoutes,
}: {
  view: View;
  routes?: RouteObject[];
}) => {
  const workbench = useService(WorkbenchService).workbench;

  const handleOnFocus = useCallback(() => {
    workbench.active(view);
  }, [workbench, view]);

  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (containerRef.current) {
      const element = containerRef.current;
      element.addEventListener('pointerdown', handleOnFocus, {
        capture: true,
      });
      return () => {
        element.removeEventListener('pointerdown', handleOnFocus, {
          capture: true,
        });
      };
    }
    return;
  }, [handleOnFocus]);

  // Floating controls outside the view (the AI island) position on its container.
  useLayoutEffect(() => {
    view.element$.next(containerRef.current);
    return () => view.element$.next(null);
  }, [view]);

  return (
    <div
      className={styles.workbenchViewContainer}
      ref={containerRef}
      data-workbench-view
    >
      <ViewRoot routes={routes} key={view.id} view={view} />
      <ViewCopilotOverlayTarget view={view} />
    </div>
  );
};

/** Where the sidebar's slide stands, and how the views follow it. */
interface SidebarSlide extends PanelSlide {
  layout: PanelLayoutTransition;
}

const WorkbenchSidebar = ({
  onSlide,
  overlaying,
}: {
  onSlide: (slide: SidebarSlide) => void;
  /** It lies over the last view's end (over a canvas) rather than beside it. */
  overlaying: boolean;
}) => {
  const t = useI18n();
  const { clientBorder } = useAtomValue(appSettingAtom);

  const [resizing, setResizing] = useState(false);

  const workbench = useService(WorkbenchService).workbench;
  const sidebarWidth = useLiveData(workbench.sidebarWidth$);
  const [width, setWidth] = useState(workbench.sidebarWidth$.value ?? 0);
  const hadSidebarOpenPreferenceOnMount = useRef(
    workbench.hasSidebarOpenPreference()
  );

  const views = useLiveData(workbench.views$);
  const activeView = useLiveData(workbench.activeView$);
  const sidebarOpen = useLiveData(workbench.sidebarOpen$);
  const layoutTransition = useLiveData(workbench.sidebarLayoutTransition$);
  const handleSlide = useCallback(
    (slide: PanelSlide) => onSlide({ ...slide, layout: layoutTransition }),
    [layoutTransition, onSlide]
  );
  const floating = useSidebarFloats();
  // What floats over the covered view keeps clear of the width rendered now, also mid-drag.
  useSidebarCover(
    views,
    overlaying ? dockedSidebarWidth(width) : 0,
    overlaying && resizing
  );
  const [showFirstRunTapHint, setShowFirstRunTapHint] = useState(false);

  const onWidthChanged = useCallback(
    (width: number) => {
      workbench.setSidebarWidth(width);
      setWidth(width);
    },
    [workbench]
  );

  const handleOpenChange = useCallback(
    (open: boolean) => {
      setShowFirstRunTapHint(false);
      if (open) {
        workbench.openSidebar();
      } else {
        workbench.closeSidebar();
      }
    },
    [workbench]
  );

  const handleFloatingMaskClick = useCallback(() => {
    setShowFirstRunTapHint(false);
    workbench.closeSidebar();
  }, [workbench]);

  useEffect(() => {
    if (resizing) return;
    setWidth(sidebarWidth ?? 0);
  }, [resizing, sidebarWidth]);

  useEffect(() => {
    setShowFirstRunTapHint(
      floating &&
        Boolean(sidebarOpen) &&
        !hadSidebarOpenPreferenceOnMount.current
    );
  }, [floating, sidebarOpen]);

  return (
    <>
      <SidebarFloatingMask
        floating={floating}
        open={sidebarOpen ?? false}
        testId="workbench-sidebar-float-mask"
        onClick={handleFloatingMaskClick}
      />
      {showFirstRunTapHint ? (
        <button
          type="button"
          aria-label={t['com.streamlain.cmdk.affine.right-sidebar.collapse']()}
          className={styles.workbenchSidebarFirstRunTapHint}
          onClick={handleFloatingMaskClick}
        />
      ) : null}
      <ResizePanel
        floating={floating}
        resizeHandlePos="left"
        resizeHandleOffset={clientBorder && sidebarOpen ? 3 : 0}
        width={width}
        resizing={resizing}
        onResizing={setResizing}
        className={styles.workbenchSidebar}
        data-client-border={clientBorder && sidebarOpen}
        open={sidebarOpen ?? false}
        layoutTransition={layoutTransition}
        onSlide={handleSlide}
        onOpen={handleOpenChange}
        onWidthChange={setWidth}
        onWidthChanged={onWidthChanged}
        minWidth={MIN_SIDEBAR_WIDTH}
        maxWidth={MAX_SIDEBAR_WIDTH}
        unmountOnExit={false}
      >
        {views.map(view => (
          <FrameworkScope key={view.id} scope={view.scope}>
            <SidebarContainer
              style={{ display: activeView !== view ? 'none' : undefined }}
            />
          </FrameworkScope>
        ))}
      </ResizePanel>
    </>
  );
};
